import React from 'react';
import { Loader2, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { getStatusTone, TONE_MARK } from '../shared/orderStatus';

/*
 * Bulk status change.
 *
 * The Apply button names the number of orders it will touch, because "Apply"
 * alone next to a picker gives no sense of blast radius. Exit statuses route
 * through the same confirmation as everywhere else — this bar could previously
 * set a hundred paid orders to Cancelled from one unguarded click.
 */
const BulkActionBar = ({
  selectedCount,
  maxPerBatch,
  statusOptions,
  value,
  onValueChange,
  onApply,
  onClear,
  isRunning,
  isEditable,
}) => {
  const overLimit = selectedCount > maxPerBatch;
  const canApply = isEditable && value != null && !isRunning && !overLimit;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="flex flex-wrap items-center gap-3 px-3 py-2.5 mb-3 rounded-xl bg-nysmah-dark text-white"
    >
      <span className="text-[12.5px] font-semibold tabular-nums">
        {selectedCount} selected
      </span>

      <Select
        value={value == null ? '' : String(value)}
        onValueChange={(next) => onValueChange(Number(next))}
        disabled={!isEditable || isRunning}
      >
        <SelectTrigger
          aria-label="Status to apply"
          className="h-8 w-auto min-w-[190px] rounded-lg border-white/20 bg-white/10 px-2.5 text-[12.5px] text-white shadow-none focus:ring-white/40"
        >
          <SelectValue placeholder="Change status to…" />
        </SelectTrigger>
        <SelectContent className="min-w-[200px]">
          {statusOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={String(option.value)}
              className="text-[12.5px]"
            >
              <span className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{
                    backgroundColor: TONE_MARK[getStatusTone(option.value)],
                  }}
                  aria-hidden="true"
                />
                {option.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {overLimit && (
        <span className="text-[11.5px] font-medium text-[#F2D69A]">
          Select at most {maxPerBatch} to apply in one batch.
        </span>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={onApply}
          disabled={!canApply}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold rounded-lg border-0 bg-white text-nysmah-dark cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {isRunning && (
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          )}
          {isRunning
            ? 'Applying…'
            : `Apply to ${selectedCount} order${selectedCount === 1 ? '' : 's'}`}
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={isRunning}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-[12.5px] rounded-lg border-0 bg-transparent text-white/70 hover:text-white hover:bg-white/10 cursor-pointer transition-colors disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
          Clear
        </button>
      </div>
    </div>
  );
};

export default BulkActionBar;
